import Button from '../../../components/common/button/Button';
import Input from '../../../components/common/input/Input';
import Calendar from '../../../components/common/calendar/Calendar';
import Modal from 'react-modal';
import React, {useState} from 'react';
import styles from './BusinessNumberPopUp.module.css';
import {authenticateAPI} from "../../../services/auth";

interface FormErrors {
    businessNumber?: string;
    name?: string;
}

interface BusinessNumberPopUpProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (businessNumber: string, name: string, companyName: string) => void;
}

const formatDateToLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

//사업자등록번호 인증 팝업
const BusinessNumberPopUp: React.FC<BusinessNumberPopUpProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     onSuccess
                                                                 }) => {
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [dateInputValue, setDateInputValue] = useState<string>(new Date().toISOString().split('T')[0]); // 별도 상태 추가
    const [businessNumber, setBusinessNumber] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [companyName, setCompanyName] = useState<string>('');
    const [errors, setErrors] = useState<FormErrors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    //사업자등록번호 인증
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        const newErrors: FormErrors = {};

        //입력 확인
        if (!businessNumber.trim()) {
            newErrors.businessNumber = '사업자등록번호를 입력하세요';
        }
        if (!name.trim()) {
            newErrors.name = '대표자 성명을 입력하세요';
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setIsLoading(true);
        try {
            //사업자등록번호 인증 API
            const response = await authenticateAPI(businessNumber, name, companyName, selectedDate)
            onSuccess(response.businessNumber, response.name, response.companyName);
            onClose();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    //사업자등록번호 입력
    const handleBusinessNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setBusinessNumber(e.target.value);
        setErrors(prev => ({...prev, businessNumber: undefined}));
    };

    //대표자명 입력
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setName(e.target.value);
        setErrors(prev => ({...prev, name: undefined}));
    };

    //회사명 입력
    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCompanyName(e.target.value);
        setErrors(prev => ({...prev, companyName: undefined}));
    };

    //사업자등록번호 형식화 (자동으로 하이픈 추가)
    const formatBusinessNumber = (businessNumber: string) => {
        // 숫자만 추출
        const numbers = businessNumber.replace(/[^\d]/g, '');

        // 사업자등록번호 형식: 000-00-00000 (3-2-5 자리)
        if (numbers.length <= 3) {
            return numbers;
        } else if (numbers.length <= 5) {
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        } else {
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
        }
    };

    // 날짜 입력 처리 (자동 하이픈 추가)
    const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        let inputValue = e.target.value;

        // 숫자만 추출
        const numbersOnly = inputValue.replace(/[^\d]/g, '');

        // 자동 포맷팅 (YYYY-MM-DD)
        let formattedValue = '';
        if (numbersOnly.length <= 4) {
            formattedValue = numbersOnly;
        } else if (numbersOnly.length <= 6) {
            formattedValue = numbersOnly.slice(0, 4) + '-' + numbersOnly.slice(4);
        } else {
            formattedValue = numbersOnly.slice(0, 4) + '-' +
                numbersOnly.slice(4, 6) + '-' +
                numbersOnly.slice(6, 8);
        }

        // 최대 10자리 제한 (YYYY-MM-DD)
        if (formattedValue.length > 10) {
            formattedValue = formattedValue.slice(0, 10);
        }

        setDateInputValue(formattedValue);

        // 완전한 날짜 형식인지 확인 (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(formattedValue)) {
            const newDate = new Date(formattedValue);
            if (!isNaN(newDate.getTime())) {
                setSelectedDate(newDate);
            }
        }
    };

    // Calendar에서 날짜 선택 시
    const handleCalendarDateSelect = (date: Date): void => {
        setSelectedDate(date);
        setDateInputValue(formatDateToLocal(date));
    };

    // 포커스 벗어날 때 유효성 검사
    const handleDateInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (!dateRegex.test(inputValue) || inputValue.trim() === '') {
            // 유효하지 않은 형식이면 현재 selectedDate로 복원
            setDateInputValue(selectedDate.toISOString().split('T')[0]);
        } else {
            const newDate = new Date(inputValue);
            if (isNaN(newDate.getTime())) {
                // 유효하지 않은 날짜면 현재 selectedDate로 복원
                setDateInputValue(selectedDate.toISOString().split('T')[0]);
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
        >
            <div className={styles.container}>
                <div className={styles.contentContainer}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>사업자등록번호 인증</h1>
                        {/*사업자등록번호 인증 팝업 닫기 버튼*/}
                        <button
                            onClick={onClose}
                            className={styles.closeButton}
                            type="button"
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} noValidate>
                        {/*사업자등록번호 입력창*/}
                        <Input
                            label="사업자등록번호"
                            name="businessNumber"
                            value={businessNumber}
                            onChange={handleBusinessNumberChange}
                            placeholder="사업자등록번호 입력"
                            fullWidth
                            required
                            disabled={isLoading}
                            error={errors.businessNumber}
                            style={{marginBottom: '1rem'}}
                        />
                        {/*대표자 성명 입력창*/}
                        <Input
                            label="대표자 성명"
                            name="name"
                            value={name}
                            onChange={handleNameChange}
                            placeholder="대표자 성명 입력"
                            fullWidth
                            required
                            disabled={isLoading}
                            error={errors.name}
                            style={{marginBottom: '1rem'}}
                        />
                        {/*회사명 입력창*/}
                        <Input
                            label="회사명"
                            name="companyName"
                            value={companyName}
                            onChange={handleCompanyNameChange}
                            placeholder="사업자등록증 기준 회사명 입력"
                            fullWidth
                            required
                            disabled={isLoading}
                            error={errors.name}
                            style={{marginBottom: '1rem'}}
                        />
                        {/*개업일자 선택*/}
                        <label className={styles.label}>
                            개업연월일
                        </label>
                        <Input
                            type="text"
                            name="openingDate"
                            value={dateInputValue}
                            onChange={handleDateInputChange}
                            onBlur={handleDateInputBlur}
                            onKeyDown={(e) => {
                                // 숫자, 백스페이스, 딜리트, 탭, 화살표 키만 허용
                                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter'];
                                if (!allowedKeys.includes(e.key) && !/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            placeholder="YYYY-MM-DD"
                            maxLength={10}
                            fullWidth
                            disabled={isLoading}
                            style={{
                                marginBottom: '1rem'
                            }}
                        />
                        <Calendar
                            selectedDate={selectedDate}
                            onDateSelect={handleCalendarDateSelect}
                        />
                        {/*인증 버튼*/}
                        <Button
                            type="submit"
                            variant="primary"
                            size="large"
                            fullWidth
                            disabled={isLoading}
                        >
                            {isLoading ? '인증중...' : '인증하기'}
                        </Button>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default BusinessNumberPopUp;